import {
  FIELD_LEVEL,
  FILE_SUFFIX,
  isSealedField,
  sealField,
  sealFile,
  sealJson,
  type ContentField,
  type ContentKey,
} from '../../shared/contentCrypto';
import { getContentKeyInfo, type EncryptionLevel } from './contentKey';

/**
 * İstemcinin toplantı içeriğini yazarken kullandığı mühürleyici (veri koruma programı
 * Faz 2). "İçerik şifreleme" kapalıysa değerler olduğu gibi geçer; açıksa tn1 ile
 * şifrelenir. Zaten şifreli değer ikinci kez şifrelenmez. Her alan kendi seviyesinden
 * itibaren şifrelenir (FIELD_LEVEL). Kişi ve şirket satırlarında `meetingId` yerine satırın
 * kendi kimliği verilir. Sunucudaki karşılığı: web/lib/crypto/seal-content.ts.
 */
export interface ContentSealer {
  readonly enabled: boolean;
  /** `options.enc`'e yazılacak seviye (EncryptionLevel). */
  readonly level: EncryptionLevel;
  field(field: ContentField, value: string | null | undefined): Promise<string | null>;
  json<T>(field: ContentField, value: T): Promise<T | string>;
  /** Depo anahtarı: şifreliyse `.tn1` ekli. */
  storageKey(baseKey: string): string;
  /** Depoya yazılacak içerik: şifreliyse tn1 dosyası. `name`: `audio.mp3` gibi. */
  file(name: string, bytes: Uint8Array): Promise<Uint8Array>;
}

const PLAIN: ContentSealer = {
  enabled: false,
  level: 0,
  field: async (_field, value) => value ?? null,
  json: async (_field, value) => value,
  storageKey: (baseKey) => baseKey,
  file: async (_name, bytes) => bytes,
};

export async function contentSealer(userId: string, meetingId: string): Promise<ContentSealer> {
  // Durum öğrenilemezse düz yaz (kayıt kaybolmasın); sunucudaki dönüştürme işi sonra şifreler.
  const info = await getContentKeyInfo(userId).catch((err: Error) => {
    console.warn('[crypto] şifreleme durumu alınamadı, düz yazılıyor:', err.message);
    return null;
  });
  if (!info?.writeEncrypted || !info.key) return PLAIN;
  const key = info.key;
  const dek: ContentKey = key;
  const level: EncryptionLevel = info.level === 3 ? 3 : info.level === 2 ? 2 : 1;
  const ctx = (field: ContentField) => ({ userId, meetingId, field });
  const skip = (field: ContentField, value: unknown) => value == null || isSealedField(value) || FIELD_LEVEL[field] > level;
  return {
    enabled: true,
    level,
    field: async (field, value) => (skip(field, value) ? (value ?? null) : sealField(dek, ctx(field), value!)),
    json: async (field, value) => (skip(field, value) ? value : sealJson(dek, ctx(field), value)),
    storageKey: (baseKey) => (baseKey.endsWith(FILE_SUFFIX) ? baseKey : `${baseKey}${FILE_SUFFIX}`),
    file: (name, bytes) => sealFile(dek, { meetingId, name }, bytes),
  };
}
